
import { PeerComparison, Benchmark } from '../types/financial';

export const convertRevenueToCrores = (revenue: number): number => {
  return revenue / 10000000; // Convert from actual value to crores
};

export const formatCurrency = (value: number): string => `₹${value.toFixed(2)} CR`;
export const formatPercentage = (value: number): string => `${value.toFixed(1)}%`;

export const sortBenchmarksByYear = (benchmarks: Benchmark[]): Benchmark[] => {
  return [...benchmarks].sort((a, b) => parseInt(a.year, 10) - parseInt(b.year, 10));
};

export const getAvailableYears = (benchmarks: Benchmark[]): string[] => {
  return sortBenchmarksByYear(benchmarks).map((b) => b.year);
};

export const getBenchmarkForYear = (
  benchmarks: Benchmark[],
  year: string
): Benchmark | undefined => benchmarks.find((b) => b.year === year);

export const isValidMetricNumber = (value: unknown): value is number => {
  return typeof value === "number" && !Number.isNaN(value);
};

export const hasMetricValue = (
  benchmark: Benchmark | undefined,
  key: string,
  medianKey: string
): boolean => {
  if (!benchmark) return false;
  const companyValue = benchmark[key as keyof Benchmark];
  const medianValue = benchmark[medianKey as keyof Benchmark];
  return isValidMetricNumber(companyValue) || isValidMetricNumber(medianValue);
};

export const getCompanyData = (peers: any[], companyName: string = "MILKLANE DAIRY SERVICES PRIVATE LIMITED") => {
  return peers.find(peer => peer.legalName.includes(companyName));
};

export const validateBenchmarkData = (benchmark: Benchmark): boolean => {
  return !!benchmark.year;
};

export const getMetricDefinitions = () => [
  { 
    key: 'revenue', 
    medianKey: 'median_revenue', 
    label: 'Revenue (₹ Crore)', 
    format: (val: number) => val.toFixed(2),
    suffix: ''
  },
  { 
    key: 'revenue_growth', 
    medianKey: 'median_revenue_growth', 
    label: 'Revenue Growth (%)', 
    format: (val: number) => val.toFixed(1),
    suffix: '%'
  },
  { 
    key: 'ebitda_margin', 
    medianKey: 'median_ebitda_margin', 
    label: 'EBITDA Margin (%)', 
    format: (val: number) => val.toFixed(1),
    suffix: '%'
  },
  { 
    key: 'net_margin', 
    medianKey: 'median_net_margin', 
    label: 'Net Margin (%)', 
    format: (val: number) => val.toFixed(1),
    suffix: '%'
  },
  { 
    key: 'return_on_equity', 
    medianKey: 'median_return_on_equity', 
    label: 'Return on Equity (%)', 
    format: (val: number) => val.toFixed(1),
    suffix: '%'
  },
  { 
    key: 'debt_by_equity', 
    medianKey: 'median_debt_by_equity', 
    label: 'Debt / Equity', 
    format: (val: number) => val.toFixed(1),
    suffix: ''
  },
  { 
    key: 'inventory_holding_period', 
    medianKey: 'median_inventory_holding_period', 
    label: 'Inventory / Sales (Days)', 
    format: (val: number) => Math.round(val),
    suffix: ' days'
  },
  { 
    key: 'debtor_days_outstanding', 
    medianKey: 'median_debtor_days_outstanding', 
    label: 'Debtors / Sales (Days)', 
    format: (val: number) => Math.round(val),
    suffix: ' days'
  },
  { 
    key: 'trade_payable_days', 
    medianKey: 'median_trade_payable_days', 
    label: 'Payables / Sales (Days)', 
    format: (val: number) => Math.round(val),
    suffix: ' days'
  },
  { 
    key: 'cash_conversion_cycle', 
    medianKey: 'median_cash_conversion_cycle', 
    label: 'Cash Conversion Cycle (Days)', 
    format: (val: number) => Math.round(val),
    suffix: ' days'
  },
  { 
    key: 'sales_by_net_fixed_assets', 
    medianKey: 'median_sales_by_net_fixed_assets', 
    label: 'Sales / Net Fixed Assets', 
    format: (val: number) => val.toFixed(1),
    suffix: ''
  }
];
